import { Alert, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { AuthoritativeFinancialResultMetadata } from '@finance/contracts';
import { translateRuntimeTKey } from '../../i18n/resolve-key';

export interface FinancialDataQualityNoticeProps {
  metadata?: AuthoritativeFinancialResultMetadata;
  state?: string;
  sourceAsOf?: string | null;
  compact?: boolean;
}

function normalizedState(metadata: AuthoritativeFinancialResultMetadata | undefined, state: string | undefined): string {
  const candidates = new Set(
    [metadata?.dataQuality.freshness, state].filter(
      (candidate): candidate is string => Boolean(candidate),
    ),
  );
  for (const candidate of [
    'UNAVAILABLE',
    'PARTIAL',
    'STALE',
    'DELAYED',
    'CURRENT',
  ]) {
    if (candidates.has(candidate)) return candidate;
  }
  return state ?? metadata?.dataQuality.freshness ?? 'UNAVAILABLE';
}

function severity(state: string): 'info' | 'warning' | 'error' | 'success' {
  if (state === 'UNAVAILABLE') return 'error';
  if (state === 'PARTIAL' || state === 'STALE' || state === 'DELAYED') return 'warning';
  if (state === 'CURRENT') return 'success';
  return 'info';
}

function messageKey(state: string): string {
  switch (state) {
    case 'CURRENT': return 'financialDataQuality:current';
    case 'DELAYED': return 'financialDataQuality:delayed';
    case 'STALE': return 'financialDataQuality:stale';
    case 'PARTIAL': return 'financialDataQuality:partial';
    case 'UNAVAILABLE': return 'financialDataQuality:unavailable';
    default: return 'financialDataQuality:unavailable';
  }
}

export function FinancialDataQualityNotice({
  metadata,
  state,
  sourceAsOf,
  compact = false,
}: FinancialDataQualityNoticeProps) {
  const { t } = useTranslation();
  const resolvedState = normalizedState(metadata, state);
  const asOf = metadata?.asOfTimestamp ?? sourceAsOf;

  if (compact && resolvedState === 'CURRENT') return null;

  const qualityMessage = translateRuntimeTKey(t, messageKey(resolvedState));

  return (
    <Alert severity={severity(resolvedState)} role="status">
      <Stack spacing={0.5}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {qualityMessage}
        </Typography>
        {!compact && metadata && (
          <Typography variant="caption" color="text.secondary">
            {t('financialDataQuality:sourceBook')}: {metadata.sourceBook}
            {' · '}{t('financialDataQuality:asOf')}: {asOf ?? '—'}
            {' · '}{t('financialDataQuality:calculationVersion')}: {metadata.lineage.calculationVersion}
            {' · '}{t('financialDataQuality:restatement')}: {metadata.restatementState}
          </Typography>
        )}
      </Stack>
    </Alert>
  );
}
